/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useCallback, useContext, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { postHttp } from 'src/api'
import { PATH_API } from 'src/helpers'
import { getQueryParameters } from 'src/helpers/tool'
import { UseFloralPlanningResult } from './types'
import { QuizQuestionContext, SCREEN_QUIZ_QUESTION } from 'src/pages/quiz/contextAPI'
import { API, ISubmitQuizQuestionPayload, ISubmitQuizQuestionResponse } from 'src/types'
import { EVENTS_FB_PIXEL, fbEvent, PATH_URL as PATH_URL_HELPER } from 'src/helpers'
import { SESSION_NAME_LOCAL, useAppContext } from 'src/helpers/app'
import { ACTION_APP, ga4EventApp, gaEventApp } from 'src/helpers/googleAnalyticsService'
import { SegmentAction, segmentTracking } from 'src/helpers/segment'
import { vwoEvent } from 'src/helpers/vwo'
import { ENV } from 'src/config/env'
import { tiktokEvent, EVENTS_TIKTOK_PIXEL, tiktokIdentify } from 'src/helpers/tiktokPixelService'
import { bingTrackConversion } from 'src/helpers/bingAdsService'
import { createHash } from 'crypto'

interface GuineaPigConfigValue {
  enabled: boolean
  budget: {
    enabled: boolean
    min: number
    max: number
  }
  colorPalette: {
    enabled: boolean
    colorPaletteIds: number[]
  }
  planningStage: {
    enabled: boolean
    planningStages: string[]
  }
}

const MAX_TIME_TRACKING = 1500
const MAX_TIME_GOOGLE_TRACKING = 3000

const submitLastQuizResponseAPI = (submitData: ISubmitQuizQuestionPayload) => {
  return postHttp(PATH_API.postSubmitLastStep, submitData)
}

const getShowGuineaPig = (
  budget: number,
  planningStage: string,
  paletteId: number,
  config: GuineaPigConfigValue | undefined
) => {
  if (!config || !config.enabled) return false

  return (
    (!config.budget.enabled || (budget >= config.budget.min && budget <= config.budget.max)) &&
    (!config.colorPalette.enabled || config.colorPalette.colorPaletteIds.includes(paletteId || -1)) &&
    (!config.planningStage.enabled || config.planningStage.planningStages.includes(planningStage || ''))
  )
}

export const FLORAL_PLANNING_OPTIONS = [
  {
    id: 'ready-to-book',
    title: 'Ready to book\nour florist',
  },
  {
    id: 'comparing-florists',
    title: 'Comparing\npotential florists',
  },
  {
    id: 'exploring-style',
    title: 'Exploring our style\nand vision',
  },
]

export const useFloralPlanning = (): UseFloralPlanningResult => {
  const [selectedOption, setSelectedOption] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [showMetBeforePopup, setShowMetBeforePopup] = useState(false)
  const history = useHistory()

  const { setQuizScreen, setRedirectUrl } = useContext(QuizQuestionContext)
  const { sessionID, storeURLParameters, handleChangeAppData } = useAppContext()

  // Check for collectionId parameter and save it to collectionsQuizEntry
  useEffect(() => {
    const params = getQueryParameters()
    const collectionId = params.get('collectionId')

    if (collectionId) {
      console.log('Collection ID detected in FloralPlanningPage, saving to collectionsQuizEntry')
      const collectionsQuizEntry = JSON.parse(localStorage.getItem('collectionsQuizEntry') || '{}')
      collectionsQuizEntry.collectionId = collectionId
      localStorage.setItem('collectionsQuizEntry', JSON.stringify(collectionsQuizEntry))
    }
  }, [])

  const gTagSetUserData = useCallback((param: any) => {
    const report = (window as { [key: string]: any })['gtag']
    if (report) {
      report('set', 'user_data', param)
    }
  }, [])

  const handleOptionSelect = useCallback((optionId: string) => {
    setSelectedOption(optionId)

    // Save to localStorage immediately when user selects an option
    const collectionsQuizEntry = JSON.parse(localStorage.getItem('collectionsQuizEntry') || '{}')

    const quizResponses = collectionsQuizEntry.quizResponses || []
    const planningStageIndex = quizResponses.findIndex((item: any) => item.key === 'PLANNING_STAGE')
    const updatedQuizResponses =
      planningStageIndex > -1
        ? quizResponses.map((item: any, index: number) =>
            index === planningStageIndex ? { ...item, values: optionId } : item
          )
        : [...quizResponses, { key: 'PLANNING_STAGE', values: optionId }]

    collectionsQuizEntry.quizResponses = updatedQuizResponses

    // Ensure collectionId is saved if present
    const params = getQueryParameters()
    const collectionId = params.get('collectionId')
    if (collectionId) {
      collectionsQuizEntry.collectionId = collectionId
    }

    localStorage.setItem('collectionsQuizEntry', JSON.stringify(collectionsQuizEntry))

    console.log('Updated collectionsQuizEntry:', collectionsQuizEntry)
  }, [])

  const tracking = async (trackValue: number) => {
    // Identify user for TikTok with hashed email
    const collectionsQuizEntry = JSON.parse(localStorage.getItem('collectionsQuizEntry') || '{}')
    const userEmail = collectionsQuizEntry.customerInfo?.emailAddress || collectionsQuizEntry.email || ''
    if (userEmail) {
      tiktokIdentify(userEmail)
    }

    const gAdwords = (param: any) =>
      new Promise((rs) => {
        const report = (window as { [key: string]: any })['gtag']
        if (report) {
          report('event', 'conversion', param)
        }
        setTimeout(rs, MAX_TIME_GOOGLE_TRACKING)
      })

    const ga4Event = (action: ACTION_APP, params: any = {}) =>
      new Promise((rs) => {
        ga4EventApp(action, { ...params, event_callback: rs })
        setTimeout(rs, MAX_TIME_TRACKING)
      })

    const _segmentTracking = (action: string) =>
      new Promise((rs) => {
        segmentTracking(action, {}, {}, rs)
        setTimeout(rs, MAX_TIME_TRACKING)
      })

    const pintrk = () =>
      new Promise((rs) => {
        const report = (window as { [key: string]: any })['pintrk']
        if (report) {
          report('track', 'lead', { value: trackValue, currency: 'USD' })
        }
        setTimeout(rs, MAX_TIME_TRACKING)
      })

    // Cheat: timeout for lib don't support callback
    const funcNoCallback = () =>
      new Promise((rs) => {
        gaEventApp(ACTION_APP.QUIZ_SUBMITTED)
        gaEventApp(ACTION_APP.FULLY_QUALIFIED_LEAD)
        fbEvent(EVENTS_FB_PIXEL.QUIZ2COMPLETE, {
          value: trackValue,
          currency: 'USD',
        })
        vwoEvent()
        setTimeout(rs, MAX_TIME_TRACKING)
      })

    const promiseFunc = [
      gAdwords({
        value: trackValue,
        currency: 'USD',
        send_to: `${ENV.REACT_APP_GOOGLE_AW_CONVERSION}/${ENV.REACT_APP_GOOGLE_AW_CONVERSION_COMPLETE_QUIZ_EVENT_ID}`,
      }),
      gAdwords({
        value: trackValue,
        currency: 'USD',
        send_to: `${ENV.REACT_APP_GOOGLE_AW_CONVERSION_2}/${ENV.REACT_APP_GOOGLE_AW_CONVERSION_LEAD_ID}`,
      }),
      ga4Event(ACTION_APP.QUIZ_SUBMITTED),
      ga4Event(ACTION_APP.FULLY_QUALIFIED_LEAD),
      _segmentTracking(SegmentAction.SQ_QUIZ_SUBMITTED),
      _segmentTracking(SegmentAction.SQ_FULLY_QUALIFIED_LEAD),
      pintrk(),
      funcNoCallback(),
      // Add TikTok form submission conversion
      new Promise((rs) => {
        const sessionId = sessionID || JSON.parse(localStorage.getItem('sessionID') || '')
        tiktokEvent(EVENTS_TIKTOK_PIXEL.SUBMIT_FORM, {
          value: trackValue,
          currency: 'USD',
          event_id: createHash('sha256').update(sessionId.toLowerCase().trim()).digest('hex'),
        })
        setTimeout(rs, MAX_TIME_TRACKING)
      }),
      // Add Bing Ads conversion tracking
      new Promise((rs) => {
        try {
          bingTrackConversion(trackValue)
        } catch (error) {
          console.error('Error tracking Bing conversion:', error)
        }
        setTimeout(rs, MAX_TIME_TRACKING)
      }),
    ]
    await Promise.all(promiseFunc)
  }

  const handleLastStepSubmit = async (payload: any) => {
    try {
      const calculateStyleResult: API<ISubmitQuizQuestionResponse> = await submitLastQuizResponseAPI(payload)

      // if the API return hadProposal === true, open the Met Before Popup
      if (calculateStyleResult?.data?.proposal?.hadProposal || calculateStyleResult?.data?.hadProposal === true) {
        setShowMetBeforePopup(true)
        return
      } else {
        setShowMetBeforePopup(false)
      }

      const collectionsQuizEntry = JSON.parse(localStorage.getItem('collectionsQuizEntry') || '{}')
      const paletteId = collectionsQuizEntry.selectedPalette?.id || 0
      const budgetValue = collectionsQuizEntry.expectedBudget
      const planningStage = collectionsQuizEntry.floralPlanning

      const showAfterQuiz = getShowGuineaPig(
        budgetValue,
        planningStage,
        +paletteId,
        calculateStyleResult.data.guineaPigConfig?.value
      )

      const leadValueConfig = calculateStyleResult.data.leadValueConfig
      const leadValuesByBudget = leadValueConfig?.leadValuesByBudget?.sort((a, b) => a.budget - b.budget) || []
      const leadValueToIndex = leadValuesByBudget.findIndex((l) => l.budget >= +budgetValue)
      const leadValueFrom = leadValueToIndex > 0 ? leadValuesByBudget[leadValueToIndex - 1] : undefined
      const leadValueTo = leadValueToIndex >= 0 ? leadValuesByBudget[leadValueToIndex] : undefined
      let leadValue: any
      if (leadValueFrom && leadValueTo) {
        const percent = (+budgetValue - leadValueFrom.budget) / (leadValueTo.budget - leadValueFrom.budget)
        if (leadValueFrom.value > leadValueTo.value) {
          leadValue = leadValueFrom.value - (leadValueFrom.value - leadValueTo.value) * percent
        } else {
          leadValue = (leadValueTo.value - leadValueFrom.value) * percent + leadValueFrom.value
        }
      }

      const customerFromLocal = collectionsQuizEntry || null

      if (calculateStyleResult.data.proposalUrl) {
        try {
          gTagSetUserData({
            email: customerFromLocal.email || '',
            address: {
              firstName: customerFromLocal.firstName || '',
              lastName: customerFromLocal.lastName || '',
            },
          })
          await tracking(leadValue)
          console.log('tracking success')
        } catch (error) {}

        if (showAfterQuiz) {
          localStorage.setItem('collectionsQuizEntry', '{}')
          setRedirectUrl(calculateStyleResult.data.proposalUrl)
          setQuizScreen(SCREEN_QUIZ_QUESTION.AFTER_QUIT_SUBMIT)
        } else {
          localStorage.setItem(SESSION_NAME_LOCAL, '')
          localStorage.setItem('collectionsQuizEntry', '{}')
          window.location.href = calculateStyleResult.data.proposalUrl
        }
      } else {
        if (showAfterQuiz) {
          localStorage.setItem('collectionsQuizEntry', '{}')
          setRedirectUrl(calculateStyleResult?.data?.redirectLink || `https://www.poppyflowers.com/`)
          setQuizScreen(SCREEN_QUIZ_QUESTION.AFTER_QUIT_SUBMIT)
        } else {
          localStorage.setItem(SESSION_NAME_LOCAL, '')
          localStorage.setItem('collectionsQuizEntry', '{}')
          window.location.href = calculateStyleResult?.data?.redirectLink || `https://www.poppyflowers.com/`
        }
      }

      // Cheat: Single loading state during quiz submission
      if (showAfterQuiz) {
        handleChangeAppData({ countLoadingTestimonial: -1 })
      }
    } catch (error: any) {
      console.log('[handleSubmit]', error)
      handleChangeAppData({ countLoadingTestimonial: -1 })
      alert(error?.data?.message ?? 'Submit failed')
      if (error.data && error.data.code === 'SESSION_DOES_NOT_EXIST') {
        history.push(PATH_URL_HELPER.SWIPING)
      }
    }
  }

  const handleSubmit = useCallback(async () => {
    if (!selectedOption) return
    setLoading(true)
    try {
      const collectionsQuizEntry = JSON.parse(localStorage.getItem('collectionsQuizEntry') || '{}')

      // build payload for API
      const payload = {
        sessionId: collectionsQuizEntry.sessionID,
        urlParameters: storeURLParameters ?? undefined,
        customerInfo: {
          firstName: collectionsQuizEntry.customerInfo?.firstName || '',
          lastName: collectionsQuizEntry.customerInfo?.lastName || '',
          partnerName: collectionsQuizEntry.customerInfo?.partnerName || '',
          emailAddress: collectionsQuizEntry.customerInfo?.emailAddress || '',
        },
        quizResponses: collectionsQuizEntry.quizResponses || [],
        eventVenues:
          Array.isArray(collectionsQuizEntry.eventVenues) && collectionsQuizEntry.eventVenues.length > 0
            ? collectionsQuizEntry.eventVenues
            : collectionsQuizEntry.venue?.placeId
            ? [
                {
                  type: 'CEREMONY',
                  place: collectionsQuizEntry.venue.place || '',
                  city: collectionsQuizEntry.venue.city || '',
                  country: collectionsQuizEntry.venue.country || '',
                  placeId: collectionsQuizEntry.venue.placeId || '',
                  state: collectionsQuizEntry.venue.state || '',
                  streetAddress: collectionsQuizEntry.venue.streetAddress || '',
                  venue: collectionsQuizEntry.venue.venue || '',
                  zipCode: collectionsQuizEntry.venue.zipCode || '',
                },
              ]
            : [],
        colorPaletteId: collectionsQuizEntry.selectedPalette?.id,
        stemPreferences: collectionsQuizEntry.flowerSelections?.map((flower: { id: number }) => ({
          stemId: flower.id,
          value: 'LIKE',
        })),
        ...(collectionsQuizEntry.selectedCollection?.id && { proposalExperience: 'collection' }),
        ...(collectionsQuizEntry.selectedCollection?.id && {
          collectionId: !isNaN(Number(collectionsQuizEntry.selectedCollection.id))
            ? Number(collectionsQuizEntry.selectedCollection.id)
            : collectionsQuizEntry.selectedCollection.id,
        }),
      }

      // Call the new handleLastStepSubmit function instead of direct API call
      await handleLastStepSubmit(payload)
      console.log('API submission successful')
    } catch (error) {
      console.error('Error submitting floral planning data:', error)
      alert('Submit failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [selectedOption, history])

  const handleCloseMetBeforePopup = () => {
    setShowMetBeforePopup(false)
    setSelectedOption(null)
    setLoading(false)

    const collectionsQuizEntry = JSON.parse(localStorage.getItem('collectionsQuizEntry') || '{}')
    if (collectionsQuizEntry) {
      collectionsQuizEntry.email = ''
    }
    localStorage.setItem('collectionsQuizEntry', JSON.stringify(collectionsQuizEntry))
  }

  return {
    selectedOption,
    handleOptionSelect,
    handleSubmit,
    loading,
    showMetBeforePopup,
    handleCloseMetBeforePopup,
  }
}
